import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ImageOff, Loader2 } from "lucide-react";

/**
 * Loads a protected media file as a blob so <img> never needs a token in the URL.
 * Revokes the object URL on unmount to avoid leaking memory.
 */
export const AuthImage = ({ src, alt = "", className = "", fallback = null, testId }) => {
  const [url, setUrl] = useState(null);
  const [state, setState] = useState("loading");
  const objectUrl = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!src) {
        setState("error");
        return;
      }
      setState("loading");
      try {
        const res = await axios.get(src, { responseType: "blob" });
        if (cancelled) return;
        objectUrl.current = URL.createObjectURL(res.data);
        setUrl(objectUrl.current);
        setState("ready");
      } catch (err) {
        if (!cancelled) setState("error");
      }
    };
    load();
    return () => {
      cancelled = true;
      if (objectUrl.current) {
        URL.revokeObjectURL(objectUrl.current);
        objectUrl.current = null;
      }
    };
  }, [src]);

  if (state === "loading") {
    return (
      <div className={`flex items-center justify-center bg-slate-800/60 ${className}`} data-testid={testId ? `${testId}-loading` : undefined}>
        <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
      </div>
    );
  }

  if (state === "error") {
    return (
      fallback || (
        <div className={`flex items-center justify-center bg-slate-800/60 ${className}`} data-testid={testId ? `${testId}-error` : undefined}>
          <ImageOff className="w-4 h-4 text-slate-600" />
        </div>
      )
    );
  }

  return <img src={url} alt={alt} className={className} data-testid={testId} />;
};

export default AuthImage;
